import {
  TAQLAudit,
  TAQLAuditMetaData,
  TAQLAuditStatusInfo,
  TAQLAuditWithAuditIdGrouping,
  TActionTaken,
  TAqlDB,
  TAqlEvent,
  TAuditDetails,
  TAuditLog,
  TAQLStore,
  TBCTXKeysWithoutSize,
  TMasterdataItemInfoWithImages,
  TSampleBctxDetails,
  TStepsContainer,
  TBctxItemWithoutSize,
  TStepBctxSelection,
  TStepSizeSelection,
  TStepAqlSelection,
  TStepAudit,
  TAqlAuditRowWithSamples,
  TAqlAuditDataToStepContainerMappingData,
  TAqlListingPagesType,
  TAqlListingLogsPayloadData,
  TREQUEST_AuditLogsDetails,
  TMeasurement_issue,
  TREQUEST_AuditSummaryFiltersData,
  TFilterSelected,
  TREQUEST_AuditSummary,
  TAqlLevelDetails,
  TAql_level,
} from '@monorepo/types';
import { StackUtil } from './stack-util';
import produce from 'immer';
import { BCTXComputationHelper } from './bctx-computation-helper';
import { FractionalNumber } from '@monorepo/utils';
import moment from 'moment';

export const AqlUtil = {
  getAqlStepContainerInitialState(): TStepsContainer {
    return {
      BCTX_SELECTION: {
        metaData: {
          stepNumber: 1,
          type: 'BCTX_SELECTION',
          id: '',
        },
      },
      ADD_AQL_LEVEL: {
        metaData: {
          stepNumber: 2,
          type: 'ADD_AQL_LEVEL',
          id: '',
        },
      },
      SIZE_SELECTION: {
        metaData: {
          stepNumber: 3,
          type: 'SIZE_SELECTION',
          id: '',
        },
      },
      ADD_QTY: {
        metaData: {
          stepNumber: 4,
          type: 'ADD_QTY',
          id: '',
        },
      },
      AUDITING: {
        metaData: {
          stepNumber: 5,
          type: 'AUDITING',
          id: '',
        },
      },
      AUDIT_RESULT: {
        metaData: {
          stepNumber: 6,
          type: 'AUDIT_RESULT',
          id: '',
        },
      },
    };
  },

  groupAQLAuditDataBasedOnAuditId(aqlAuditList: TAQLAudit[]): TAQLAuditWithAuditIdGrouping {
    const auditIdGroup = aqlAuditList.reduce((acc, aqlAuditData) => {
      const auditId = aqlAuditData.metaData.auditId;
      if (!acc[auditId]) {
        acc[auditId] = [];
      }
      acc[auditId].push(aqlAuditData);
      return acc;
    }, {} as TAQLAuditWithAuditIdGrouping);

    for (const auditId in auditIdGroup) {
      auditIdGroup[auditId].sort((a, b) => Number(b.metaData.datetime) - Number(a.metaData.datetime));
    }

    return auditIdGroup;
  },

  preProcessMeasurementIssues(measurementIssues: TMeasurement_issue[]): TMeasurement_issue[] {
    return measurementIssues.map((issue: TMeasurement_issue) => {
      let standard = issue.standard.trim();

      if (issue.uom === 'cm') {
        standard = standard.replace(' ', '');
      } else {
        // Normalize Unicode fraction slash (⁄ U+2044) to regular slash (/)
        standard = standard.replace(/\u2044/g, '/');
        standard = standard.replace(/\s+/g, ' '); //remove  if more than one space
        if (standard.includes('.')) {
          standard = standard.replace(' ', '');
          standard = FractionalNumber.getFractionalNumberFromDecimal(parseFloat(standard));
        }
      }

      return {
        ...issue,
        standard: standard,
        tolPlus: issue.tolPlus.replace(' ', ''),
        tolMinus: issue.tolMinus.replace(' ', ''),
      };
    });
  },

  getAqlGroupDataAfterMergingfrontendAndBackendData(frontendAqlList: TAQLAudit[], backendAqlList: TAQLAudit[]) {
    const frontendGroupedAuditIdAqlData = AqlUtil.groupAQLAuditDataBasedOnAuditId(frontendAqlList);
    const backendGroupedAuditIdAqlData = AqlUtil.groupAQLAuditDataBasedOnAuditId(backendAqlList);
    const mergedData: TAQLAudit[] = [];

    // Process backend data
    for (const auditId in backendGroupedAuditIdAqlData) {
      const backendAuditData = backendGroupedAuditIdAqlData[auditId];
      const frontendAuditData = frontendGroupedAuditIdAqlData[auditId] || [];

      if (frontendAuditData.length === 0) {
        // If backend data contains audit-id which doesn't have entry in frontend => we simply render it
        mergedData.push(...backendAuditData);
      } else {
        const firstFrontendEntry = frontendAuditData[0];
        const firstFrontendCycle = firstFrontendEntry.metaData.auditCycle;

        if (firstFrontendCycle === 1) {
          // If pass is of cycle 1 => all the history from frontend with audit-cycle > 0
          // If fail is of cycle 1 => all the history from frontend with audit-cycle > 0
          mergedData.push(...frontendAuditData);
        } else if (firstFrontendCycle > 1) {
          // If pass is of cycle k > 1 => fails(say k number) => this we can deduce from backend data + all the history from frontend with audit-cycle > k
          // If fail is of cycle k > 1 => fails(say k number) => this we can deduce from backend data + all the history from frontend with audit-cycle > k
          mergedData.push(
            ...backendAuditData.filter((data) => data.metaData.auditCycle < firstFrontendCycle),
            ...frontendAuditData.filter((data) => data.metaData.auditCycle >= firstFrontendCycle),
          );
        }
      }
    }

    // Process frontend data for audit IDs not present in backend
    for (const auditId in frontendGroupedAuditIdAqlData) {
      if (!backendGroupedAuditIdAqlData[auditId]) {
        // If audit-id is available in frontend and not backend, add frontend data directly to list
        mergedData.push(...frontendGroupedAuditIdAqlData[auditId]);
      }
    }

    return AqlUtil.groupAQLAuditDataBasedOnAuditId(mergedData);
  },
  getLastAuditDataAfterUndo(aqlAuditIdGroups: TAQLAuditWithAuditIdGrouping): TAQLAuditWithAuditIdGrouping {
    const aqlAuditIdGroupsAfterUndo: TAQLAuditWithAuditIdGrouping = {};
    for (const auditId in aqlAuditIdGroups) {
      const aqlAuditListAfterUndo = AqlUtil.getAqlListAfterUndoOperation(aqlAuditIdGroups[auditId]);
      if (aqlAuditListAfterUndo && aqlAuditListAfterUndo.length > 0) {
        aqlAuditIdGroupsAfterUndo[auditId] = aqlAuditListAfterUndo;
      }
    }

    return aqlAuditIdGroupsAfterUndo;
  },

  getAqlListAfterUndoOperation(aqlAuditList: TAQLAudit[]) {
    // sort in increasing datetime
    aqlAuditList.sort((a, b) => Number(a.metaData.datetime) - Number(b.metaData.datetime));
    const stack = new StackUtil<TAQLAudit>();
    let errorOccurred = false;
    try {
      aqlAuditList.forEach((aqlAudit) => {
        stack.push(aqlAudit);
        while (!stack.isEmpty()) {
          const top = stack.peek();
          if (top.metaData.action_taken === 'undo_audit' && stack.size() > 1) {
            stack.pop();
            const itemBeforeUndo = stack.peek();
            if (
              itemBeforeUndo.metaData.auditCycle !== top.metaData.auditCycle ||
              itemBeforeUndo.statusInfo.status !== top.statusInfo.status
            ) {
              // Error last item is not having same audit cycle or status not same with undo_audit
              errorOccurred = true;
              break;
            }
            stack.pop();
          } else break;
        }
      });

      if (errorOccurred) {
        return null;
      }

      return stack.items;
    } catch (error) {
      return null;
    }
  },

  getAqlAuditMappedDto(backendAuditLog: TAuditLog[]) {
    const mappedData: TAQLAudit[] = [];
    backendAuditLog.forEach((logData) => {
      let aqlLevel: TAql_level | undefined = undefined;
      if (logData.aql_level_details) {
        const aqlLevelDetails = JSON.parse(logData.aql_level_details) as TAqlLevelDetails;
        aqlLevel = {
          aql_level: aqlLevelDetails.aql_level,
          buyer: aqlLevelDetails.aql_level_buyer,
          fail_qty: aqlLevelDetails.aql_level_fail_qty,
          lot_range_end: aqlLevelDetails.aql_level_lot_range_end,
          lot_range_start: aqlLevelDetails.aql_level_lot_range_start,
          pass_qty: aqlLevelDetails.aql_level_pass_qty,
          sample: aqlLevelDetails.aql_level_sample,
        } as TAql_level;
      }
      mappedData.push({
        auditDetails: {
          sampleBctxDetails: JSON.parse(logData.buz_tags),
          sampleQtyPassFailTotalData: JSON.parse(logData.ecx_num),
          sizeBasedQtyPassFailData: logData.rows,
          aql_level: aqlLevel!,
        },
        auditSource: 'backend',
        metaData: {
          action_taken: logData.action_taken as TActionTaken,
          auditCycle: parseInt(logData.audit_cycle),
          auditId: logData.audit_id,
          datetime: logData.datetime,
          lineAndHskDetails: JSON.parse(logData.aux),
          reAuditId: logData.re_audit_id,
          auditDisplayName: JSON.parse(logData.ecx_tag).audit_disName,
        },
        statusInfo: {
          status: logData.status,
        },
        aql_level: aqlLevel!,
      });
    });

    return mappedData;
  },
  formatDateFromTimeToAPiPayloadFormat(timeNumber: number, isStart: boolean) {
    let date = new Date(timeNumber);
    let year = date.getFullYear();
    let month = ('0' + (date.getMonth() + 1)).slice(-2);
    let day = ('0' + date.getDate()).slice(-2);
    let formattedDate = year + month + day;
    if (isStart) {
      formattedDate += '0000000';
    } else {
      formattedDate += '2359000';
    }
    return formattedDate;
  },

  updateStartAndEndTimeBasedOnListingAPIPayload(startAndEndTime: { start: number; end: number }) {
    let updatedStart = AqlUtil.formatDateFromTimeToAPiPayloadFormat(startAndEndTime.start, true);
    let updatedEnd = AqlUtil.formatDateFromTimeToAPiPayloadFormat(startAndEndTime.end, false);

    return {
      start: updatedStart,
      end: updatedEnd,
    };
  },

  getAllLinesFrontendMappedAqlList(
    eventsWithLineAndStation: TAQLStore['aqlDB']['dailyCache']['completedEvents'],
    isReAudit: boolean = false,
  ) {
    let allLinesAqlAuditEvents: TAQLAudit[] = [];
    Object.keys(eventsWithLineAndStation.lineMap).map((line) => {
      Object.keys(eventsWithLineAndStation.lineMap[line].shiftMap).map((shift) => {
        allLinesAqlAuditEvents.push(...eventsWithLineAndStation.lineMap[line].shiftMap[shift]);
      });
    });
    return allLinesAqlAuditEvents;
  },
  getIdFromDefect(defect: TMasterdataItemInfoWithImages) {
    return defect.category + '_' + defect.sub_category + '_' + defect.name;
  },

  getDefectsAddedFromEvent(bulkEvent: TAqlEvent): TMasterdataItemInfoWithImages[] {
    const set = new Set<string>();
    const issues: TMasterdataItemInfoWithImages[] = [];
    bulkEvent.events[0].eventInfo.auditDetails.rows
      .filter((sample) => sample.issues.length > 0)
      .forEach((sample) => {
        sample.issues.forEach((defect) => {
          const id = AqlUtil.getIdFromDefect(defect);
          if (!set.has(id)) {
            set.add(id);
            issues.push(defect);
          }
        });
      });
    return issues;
  },

  getMergedDefectsAndClearImagesFromCurrentList(currentList: TMasterdataItemInfoWithImages[], newList: TMasterdataItemInfoWithImages[]) {
    const set = new Set<string>();
    currentList.forEach((defect) => {
      const id = AqlUtil.getIdFromDefect(defect);
      set.add(id);
    });
    return produce(currentList, (draft) => {
      draft.forEach(defect => {
        defect.capturedImages = null;
      });
      newList.forEach((defect) => {
        const id = AqlUtil.getIdFromDefect(defect);
        if (!set.has(id)) {
          set.add(id);
          draft.push({ ...defect, capturedImages: null });
        }
      });
    });
  },

  getAqlAudit(bulkEvent: TAqlEvent): TAQLAudit {
    const { subject, shift } = bulkEvent.metadata.stationDetails;

    const auditDetails = bulkEvent.events[0].eventInfo.auditDetails;
    const aqlAuditMetaData: TAQLAuditMetaData = {
      lineAndHskDetails: {
        hsk: subject ? subject.hsk : '',
        dnm: subject ? subject.dis_name : '', // line
        dnr: subject ? subject.gatev : '',
        smv: 0,
      },
      datetime: new Date().getTime(),
      auditCycle: auditDetails.metaData.auditCycle,
      action_taken: auditDetails.metaData.action_taken,
      auditId: auditDetails.metaData.auditId,
      reAuditId: auditDetails.metaData.reAuditId,
      auditDisplayName: auditDetails.metaData.auditDisplayName,
    };
    const statusInfo: TAQLAuditStatusInfo = {
      status: auditDetails.metaData.status,
    };

    const auditDetail: TAuditDetails = {
      sampleQtyPassFailTotalData: {
        fail: auditDetails.summary.fail,
        rqty: auditDetails.summary.qty,
        pass: auditDetails.summary.pass,
        qty: auditDetails.summary.qty,
        wqty: auditDetails.summary.qty,
        bqty: auditDetails.summary.qty,
        sample_qty: auditDetails.summary.sample_qty,
      },
      aql_level: bulkEvent.events[0].eventInfo.auditDetails.aql_level,
      sizeBasedQtyPassFailData: auditDetails.rows.map((row) => {
        return {
          row_id: row.row_id,
          row_total_qty: row.row_qty,
          row_sample_qty: row.row_sample,
          pass_count: row.row_pass,
          fail_count: row.row_fail,
          size: {
            name: row.size,
          },
          color: null,
          issues: row.issues.map((issue) => issue),
          samples: row.samples.map((sample) => sample),
        };
      }),
      sampleBctxDetails: bulkEvent.events[0].eventInfo.auditDetails.bctxItemListWithoutSize.reduce((acc, curr) => {
        acc[curr.k] = curr.v;
        return acc;
      }, {} as TSampleBctxDetails),
    };

    return {
      auditDetails: auditDetail,
      auditSource: 'frontend',
      statusInfo,
      metaData: aqlAuditMetaData,
      aql_level: bulkEvent.events[0].eventInfo.auditDetails.aql_level,
    };
  },

  updateCompletedEventAndRecentCategory(
    state: TAqlDB,
    bulkEvent: TAqlEvent,
    orderKeys: TBCTXKeysWithoutSize[],
  ): TAqlDB {
    const line = bulkEvent.metadata.stationDetails.subject.subject;
    const shift = bulkEvent.metadata.stationDetails.shift.id;
    const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
      orderKeys,
      bulkEvent.events[0].eventInfo.auditDetails.bctxItemListWithoutSize,
    );
    state = produce(state, (draft) => {
      if (!draft.dailyCache.recentCategoryList[bctxKey]) draft.dailyCache.recentCategoryList[bctxKey] = [];
    });
    const recentDefectsWithoutImages = AqlUtil.getMergedDefectsAndClearImagesFromCurrentList(
      state.dailyCache.recentCategoryList[bctxKey],
      AqlUtil.getDefectsAddedFromEvent(bulkEvent),
    );
    state = produce(state, (draft) => {
      draft.dailyCache.recentCategoryList[bctxKey] = recentDefectsWithoutImages;
    });
    state = produce(state, (draft) => {
      if (!draft.dailyCache.completedEvents.lineMap[line]) {
        draft.dailyCache.completedEvents.lineMap[line] = {
          shiftMap: {
            [shift]: [],
          },
        };
      } else if (!draft.dailyCache.completedEvents.lineMap[line].shiftMap[shift]) {
        draft.dailyCache.completedEvents.lineMap[line].shiftMap[shift] = [];
      }
    });
    return produce(state, (draft) => {
      const tobeUpdateAudit = AqlUtil.getAqlAudit(bulkEvent);
      draft.dailyCache.completedEvents.lineMap[line].shiftMap[shift].push(tobeUpdateAudit);
      draft.dailyCache.currentWeekFailedEvents = AqlUtil.updateCurrentWeeksFailedEvents(
        tobeUpdateAudit,
        draft.dailyCache.currentWeekFailedEvents,
      );
      draft.persistanceCache.savedEvents = AqlUtil.updateSaveEvents(tobeUpdateAudit, state);
    });
  },

  updateSaveEvents(audit: TAQLAudit, state: TAqlDB) {
    Object.entries(state.persistanceCache.savedEvents.lineMap).forEach(([line, shiftWiseSavedEventsCache]) => {
      Object.entries(shiftWiseSavedEventsCache.shiftMap).forEach(([shift, savedEventDataList]) => {
        state = produce(state, (draft) => {
          draft.persistanceCache.savedEvents.lineMap[line] = {
            shiftMap: {
              [shift]: savedEventDataList.filter((event) => event.metaData.auditId !== audit.metaData.auditId),
            },
          };
        });
      });
    });
    return state.persistanceCache.savedEvents;
  },

  updateCurrentWeeksFailedEvents(audit: TAQLAudit, currentFailedEvents: TAQLAudit[]) {
    if (audit.metaData.action_taken === 'undo_audit') {
      if (audit.statusInfo.status === 'fail') {
        return audit.metaData.auditCycle > 1
          ? currentFailedEvents
          : currentFailedEvents.filter((item) => item.metaData.auditId !== audit.metaData.auditId);
      } else {
        if (audit.metaData.auditCycle === 1) return currentFailedEvents;
        const updatedfailedAudits = currentFailedEvents.map((item) => item);
        updatedfailedAudits.push(audit);
        return updatedfailedAudits;
      }
    }

    const toBeUpdatedAuditIndex = currentFailedEvents.findIndex((item) => {
      return item.metaData.auditId === audit.metaData.auditId;
    });
    if (toBeUpdatedAuditIndex > -1) {
      const updatedfailedAudits = currentFailedEvents.filter(
        (item) => item.metaData.auditId !== audit.metaData.auditId,
      );
      if (audit.statusInfo.status === 'pass') return updatedfailedAudits;
      updatedfailedAudits.push(audit);
      return updatedfailedAudits;
    }
    const updatedfailedAudits = currentFailedEvents.map((item) => item);
    if (audit.statusInfo.status === 'fail') {
      updatedfailedAudits.push(audit);
    }

    return updatedfailedAudits;
  },

  updateSavedEventAndRecentCategory(state: TAqlDB, bulkEvent: TAqlEvent, orderKeys: TBCTXKeysWithoutSize[]): TAqlDB {
    const line = bulkEvent.metadata.stationDetails.subject.subject;
    const shift = bulkEvent.metadata.stationDetails.shift.id;
    const bctxKey = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
      orderKeys,
      bulkEvent.events[0].eventInfo.auditDetails.bctxItemListWithoutSize,
    );
    state = produce(state, (draft) => {
      if (!draft.dailyCache.recentCategoryList[bctxKey]) draft.dailyCache.recentCategoryList[bctxKey] = [];
    });
    const recentDefectsWithoutImages = AqlUtil.getMergedDefectsAndClearImagesFromCurrentList(
      state.dailyCache.recentCategoryList[bctxKey],
      AqlUtil.getDefectsAddedFromEvent(bulkEvent),
    );
    state = produce(state, (draft) => {
      draft.dailyCache.recentCategoryList[bctxKey] = recentDefectsWithoutImages;
    });
    state = produce(state, (draft) => {
      if (!draft.persistanceCache.savedEvents.lineMap[line]) {
        draft.persistanceCache.savedEvents.lineMap[line] = {
          shiftMap: {
            [shift]: [],
          },
        };
      } else if (!draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift]) {
        draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift] = [];
      }
    });
    return produce(state, (draft) => {
      const tobeUpdateAudit = AqlUtil.getAqlAudit(bulkEvent);
      const hasAlreadyAdded = draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift].findIndex((event) => {
        return event.metaData.auditId === tobeUpdateAudit.metaData.auditId;
      });
      if (hasAlreadyAdded > -1) {
        draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift][hasAlreadyAdded] = tobeUpdateAudit;
      } else {
        draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift].push(tobeUpdateAudit);
      }
    });
  },

  mapAqlAuditDataToStepContainerData(
    stepsContainerData: TStepsContainer,
    extraMappingData: TAqlAuditDataToStepContainerMappingData,
    isSavedAuditClicked: boolean,
  ) {
    const selectedBctxMap: TBctxItemWithoutSize[] = [];

    Object.keys(extraMappingData.rowData.auditDetails.sampleBctxDetails).map((key) => {
      const keyData = key as TBCTXKeysWithoutSize;
      selectedBctxMap.push({
        k: keyData,
        v: extraMappingData.rowData.auditDetails.sampleBctxDetails[keyData],
      });
    });

    stepsContainerData.BCTX_SELECTION = {
      metaData: {
        ...stepsContainerData.BCTX_SELECTION.metaData,
        id: extraMappingData.rowData.metaData.auditId,
      },
      componentSpecificData: {
        selectedBctx: selectedBctxMap,
        headerInput: BCTXComputationHelper.createBctxItemHeader(
          selectedBctxMap,
          selectedBctxMap.map((key) => key.k),
          extraMappingData.bctxKeysToDisplayMap,
        ),
      },
    } as TStepBctxSelection;

    stepsContainerData.SIZE_SELECTION = {
      metaData: {
        ...stepsContainerData.SIZE_SELECTION.metaData,
        id: extraMappingData.rowData.metaData.auditId,
      },
      componentSpecificData: {
        selectedSize: extraMappingData.rowData.auditDetails.sizeBasedQtyPassFailData.map((size) => size.size.name),
      },
    } as TStepSizeSelection;

    stepsContainerData.ADD_AQL_LEVEL = {
      metaData: {
        ...stepsContainerData.ADD_AQL_LEVEL.metaData,
        id: extraMappingData.rowData.metaData.auditId,
      },
      componentSpecificData: {
        selectedAqlLevel: extraMappingData.allAqlLevels,
      },
    } as TStepAqlSelection;

    stepsContainerData.ADD_QTY = {
      metaData: {
        ...stepsContainerData.ADD_QTY.metaData,
        id: extraMappingData.rowData.metaData.auditId,
      },
      componentSpecificData: {
        auditRows: extraMappingData.rowData.auditDetails.sizeBasedQtyPassFailData.map((ele, index) => {
          return {
            index: index,
            row_fail: isSavedAuditClicked ? ele.fail_count : 0,
            row_pass: isSavedAuditClicked ? ele.pass_count : 0,
            row_id: ele.row_id,
            row_qty: ele.row_total_qty,
            row_sample: ele.row_sample_qty,
            samples: isSavedAuditClicked ? [...ele.samples] : [],
            size: ele.size.name,
          };
        }) as TAqlAuditRowWithSamples[],
        selectedAqlLevel: extraMappingData.allAqlLevels,
      },
    } as TStepAudit;

    if (isSavedAuditClicked) {
      stepsContainerData.AUDITING = {
        metaData: {
          ...stepsContainerData.AUDITING.metaData,
          id: extraMappingData.rowData.metaData.auditId,
        },
        componentSpecificData: {
          auditRows: extraMappingData.rowData.auditDetails.sizeBasedQtyPassFailData.map((ele, index) => {
            return {
              index: index,
              row_fail: ele.fail_count,
              row_pass: ele.pass_count,
              row_id: ele.row_id,
              row_qty: ele.row_total_qty,
              row_sample: ele.row_sample_qty,
              samples: [...ele.samples],
              size: ele.size.name,
            };
          }) as TAqlAuditRowWithSamples[],
          selectedAqlLevel: [extraMappingData.rowData.aql_level],
        },
      } as TStepAudit;
    }

    return stepsContainerData;
  },

  deleteAudit(state: TAqlDB, aqlAuditMetaData: TAQLAuditMetaData): TAqlDB {
    const aduditId = aqlAuditMetaData.auditId;
    Object.entries(state.persistanceCache.savedEvents.lineMap).forEach(([line, shiftWiseSavedEventsCache]) => {
      Object.entries(shiftWiseSavedEventsCache.shiftMap).forEach(([shift, savedEventDataList]) => {
        state = produce(state, (draft) => {
          draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift] = savedEventDataList.filter(
            (event) => event.metaData.auditId !== aduditId,
          );
        });
      });
    });
    return state;
  },

  createAqlListingLogsPayload(
    listingType: TAqlListingPagesType,
    aqlListingLogsPayloadData: TAqlListingLogsPayloadData,
  ) {
    const mappedStartAndEndTime = AqlUtil.updateStartAndEndTimeBasedOnListingAPIPayload(
      aqlListingLogsPayloadData.startAndEndTime,
    );
    const payload: TREQUEST_AuditLogsDetails = {
      subject_key: aqlListingLogsPayloadData.factoryId,
      requests: [
        {
          req_id: 'hbase_logs_query_request',
          req_args: {
            filter_param_1: 'buyer',
            filter_value_1: [],
            filter_param_2: 'style',
            filter_value_2: [],
            filter_param_3: '',
            filter_value_3: [],
            filter_param_4: '',
            filter_value_4: [],
            filter_param_5: '',
            filter_value_5: [],
            filter_param_6: '',
            filter_value_6: [],
            filter_param_7: '',
            filter_value_7: [],
            filter_param_8: '',
            filter_value_8: [],
            action: '',
            reaudit: listingType === 'REAUDIT',
            subject_key: aqlListingLogsPayloadData.factoryId,
            station_id: aqlListingLogsPayloadData.allStations!.map((station) => station.subject),
            bctx: ['buyer', 'style'],
            hsk: aqlListingLogsPayloadData.allStations![0].hsk,
            start_date: mappedStartAndEndTime.start,
            end_date: mappedStartAndEndTime.end,
          },
        },
      ],
    };

    return payload;
  },
  createSummaryPageFiltersDataPayload(aqlListingLogsPayloadData: TAqlListingLogsPayloadData) {
    const startDateMapped = moment(aqlListingLogsPayloadData.startAndEndTime.start).format('YYYY-MM-DD');
    const endDateMapped = moment(aqlListingLogsPayloadData.startAndEndTime.end).format('YYYY-MM-DD');
    const payload: TREQUEST_AuditSummaryFiltersData = {
      subject_key: aqlListingLogsPayloadData.factoryId,
      requests: [
        {
          req_id: 'aql_app_log_filter_data_request',
          req_args: [
            {
              key: '@dataSource',
              value: 'ds6_analytics',
            },
            {
              key: '@searchDimensions',
              value: ['style', 'buyer', 'module_id'],
            },
            {
              key: '@sk',
              value: aqlListingLogsPayloadData.factoryId,
            },
            {
              key: '@subjects',
              value: aqlListingLogsPayloadData.allStations.map((ele) => ele.subject),
            },
            {
              key: '@moduleid_type',
              value: 'true',
            },
            {
              key: '@module_values',
              value: [],
            },
            {
              key: '@undo_filter',
              value: 'true',
            },
            {
              key: '@audit_cycle_filter',
              value: 'true',
            },
            {
              key: '@audit_status_filter',
              value: 'true',
            },
            {
              key: '@filter_type1',
              value: 'true',
            },
            {
              key: '@filter_type2',
              value: 'true',
            },
            {
              key: '@filter_type3',
              value: 'true',
            },
            {
              key: '@filter_type4',
              value: true,
            },
            {
              key: '@filter_type5',
              value: true,
            },
            {
              key: '@filter_type6',
              value: 'true',
            },
            {
              key: '@filter_type7',
              value: 'true',
            },
            {
              key: '@filter_type8',
              value: 'true',
            },
            {
              key: '@filter_dim1',
              value: 'style',
            },
            {
              key: '@filter_dim2',
              value: 'buyer',
            },
            {
              key: '@filter_dim3',
              value: 'module_id',
            },
            {
              key: '@filter_dim4',
              value: '@filter_dim4',
            },
            {
              key: '@filter_dim5',
              value: '@filter_dim5',
            },
            {
              key: '@filter_dim6',
              value: '@filter_dim6',
            },
            {
              key: '@filter_dim7',
              value: '@filter_dim7',
            },
            {
              key: '@filter_dim8',
              value: '@filter_dim8',
            },
            {
              key: '@filter_value1',
              value: [],
            },
            {
              key: '@filter_value2',
              value: [],
            },
            {
              key: '@filter_value3',
              value: [],
            },
            {
              key: '@filter_value4',
              value: '@filter_value4',
            },
            {
              key: '@filter_value5',
              value: '@filter_value5',
            },
            {
              key: '@filter_value6',
              value: '@filter_value6',
            },
            {
              key: '@filter_value7',
              value: '@filter_value7',
            },
            {
              key: '@filter_value8',
              value: '@filter_value8',
            },
            {
              key: '@start_date',
              value: startDateMapped,
            },
            {
              key: '@end_date',
              value: endDateMapped,
            },
            {
              key: '@attribute',
              value: ['carton_audit', 'aql_defect'],
            },
            {
              key: '@window',
              value: `${startDateMapped}T00:00:00.000Z/${endDateMapped}T23:59:59.999Z`,
            },
          ],
        },
      ],
    };

    return payload;
  },
  createSummaryPageDataPayload(
    selectedFilters: TFilterSelected[],
    aqlListingLogsPayloadData: TAqlListingLogsPayloadData,
  ) {
    const startDateMapped = moment(aqlListingLogsPayloadData.startAndEndTime.start).format('YYYY-MM-DD');
    const endDateMapped = moment(aqlListingLogsPayloadData.startAndEndTime.end).format('YYYY-MM-DD');

    const isLineFilterSelected = selectedFilters.find((ele) => ele.key === 'lines');
    const isStyleFilterSelected = selectedFilters.find((ele) => ele.key === 'style');
    const isBuyerFilterSelected = selectedFilters.find((ele) => ele.key === 'buyer');

    const payload: TREQUEST_AuditSummary = {
      subject_key: aqlListingLogsPayloadData.factoryId,
      requests: [
        {
          req_id: 'aql_summary_data_request',
          req_args: [
            {
              key: '@ds',
              value: 'ds6_analytics',
            },
            {
              key: '@searchDimensions',
              value: ['style', 'buyer'],
            },
            {
              key: '@dim1',
              value: '@dim1',
            },
            {
              key: '@dim2',
              value: '@dim2',
            },
            {
              key: '@window',
              value: `${startDateMapped}T00:00:00.000Z/${endDateMapped}T23:59:59.999Z`,
            },
            {
              key: '@start_date',
              value: startDateMapped,
            },
            {
              key: '@end_date',
              value: endDateMapped,
            },
            {
              key: '@filter_type1',
              value: isStyleFilterSelected ? 'in' : 'true',
            },
            {
              key: '@filter_type2',
              value: isBuyerFilterSelected ? 'in' : 'true',
            },
            {
              key: '@filter_type3',
              value: true,
            },
            {
              key: '@filter_type4',
              value: true,
            },
            {
              key: '@filter_dim1',
              value: 'style',
            },
            {
              key: '@filter_dim2',
              value: 'buyer',
            },
            {
              key: '@filter_dim3',
              value: '@filter_dim3',
            },
            {
              key: '@filter_dim4',
              value: '@filter_dim4',
            },
            {
              key: '@filter_value1',
              value: isStyleFilterSelected ? (isStyleFilterSelected.values as string[]) : [],
            },
            {
              key: '@filter_value2',
              value: isBuyerFilterSelected ? (isBuyerFilterSelected.values as string[]) : [],
            },
            {
              key: '@filter_value3',
              value: '@filter_value3',
            },
            {
              key: '@filter_value4',
              value: '@filter_value4',
            },
            {
              key: '@limit',
              value: 5,
            },
            {
              key: '@subject_key',
              value: aqlListingLogsPayloadData.factoryId,
            },
            {
              key: '@subjects',
              value: isLineFilterSelected
                ? aqlListingLogsPayloadData.allStations
                    .filter((ele) => (isLineFilterSelected.values as string[]).includes(ele.dis_name))
                    .map((ele) => ele.subject)
                : aqlListingLogsPayloadData.allStations.map((ele) => ele.subject),
            },
          ],
        },
      ],
    };

    return payload;
  },

  getReAuditIdGroups(aqlAuditIdGroups: TAQLAuditWithAuditIdGrouping) {
    const updatedAqlReAuditData: TAQLAuditWithAuditIdGrouping = {};
    for (const auditId in aqlAuditIdGroups) {
      aqlAuditIdGroups[auditId].sort((a, b) => Number(b.metaData.datetime) - Number(a.metaData.datetime));
      if (aqlAuditIdGroups[auditId][0].statusInfo.status === 'fail') {
        updatedAqlReAuditData[auditId] = [aqlAuditIdGroups[auditId][0]];
      }
    }
    return updatedAqlReAuditData;
  },

  clearCapturedImages(defects: TMasterdataItemInfoWithImages[]) {
    const List = defects.map((defect) => ({
      ...defect,
      capturedImages: null,
    }));
    return List;
  },
};
